import { createClient } from '@supabase/supabase-js';
import { parseDeviceRawConfig } from './parse-device-config';
import { getDeviceIdVariations } from '../utils/deviceUtils';

export const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://xlsxtvipjkctvwkzahmc.supabase.co';
export const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inhsc3h0dmlwamtjdHZ3a3phaG1jIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTMwMzkxNjAsImV4cCI6MjA2ODYxNTE2MH0.U_uBm4Xn6PAHDnw3pGG9NQPiWaimAEPWtgpSSP89Tmc';

export const supabase = createClient(supabaseUrl, supabaseKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Use implicit flow for email recovery links so no PKCE code_verifier is required
    flowType: 'implicit',
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

export interface GpsData {
  id: number;
  device_id: string;
  device_nickname: string;
  latitude: number;
  longitude: number;
  location_source: string;
  address: string;
  battery_voltage: number;
  battery_percentage: number;
  charging_state: string;
  temperature: number;
  humidity: number;
  pressure: number | null;
  light_level: number;
  speed: number;
  direction: number;
  altitude: number;
  mileage: number | null;
  signal_strength: number;
  satellites: number;
  device_timestamp: string;
  created_at: string;
  
  // Device identification fields (NEW)
  firmware_version?: string; // From 0xF2 extension
  iccid?: string;           // From 0xF8 extension
  raw_data?: {
    imei?: string;
    address?: string;
    location_source?: string;
    firmware?: string;      // From enhanced_aovx_bridge
    iccid?: string;         // From enhanced_aovx_bridge
    battery?: {
      voltage: number;
      percentage: number;
      charging_state: string;
    };
    sensors?: {
      temperature: number;
      humidity: number;
      light: number;
    };
    vehicle_info?: {
      speed: number;
      direction: number;
      altitude: number;
      mileage: number | null;
    };
    signal_info?: {
      strength: number;
      satellites: number;
    };
  };
}

export interface Alert {
  id: string;
  device_id: string;
  type: 'geofence' | 'geofence_entry' | 'geofence_exit' | 'low_battery' | 'offline' | 'speed' | 'panic' | string;
  message: string;
  severity: 'low' | 'medium' | 'high' | 'critical' | 'info';
  timestamp: string;
  acknowledged: boolean;
}

export interface Device {
  id: string;
  device_id: string; // IMEI
  device_nickname?: string;
  device_type?: string;
  is_active: boolean;
  last_seen?: string;
  created_at?: string;
  updated_at?: string;
  claimed_at?: string;
  
  // Device identification fields (NEW)
  firmware_version?: string; // From 0xF2 extension
  iccid?: string;           // From 0xF8 extension
  last_firmware_update?: string; // When firmware version was last updated
  
  // Network Configuration
  apn?: string;
  main_server?: string;
  main_server_port?: number;
  backup_server?: string;
  backup_server_port?: number;
  manage_server?: string;
  manage_server_port?: number;
  terminal_phone?: string;
  ntp_server?: string;
  ntp_ip?: string;
  ntp_port?: number;
  tcp_port?: number;
  timezone?: number;
  work_mode?: string;
  working_mode?: string;
  username?: string;
  password?: string;
  
  // Reporting intervals
  report_interval?: number;
  reporting_interval?: number;
  sample_interval?: number;
  stationary_interval?: number;
  report_interval_idle?: number;
  report_interval_movement?: number;
  movement_speed_threshold?: number;
  movement_distance?: number;
  movement_detection?: boolean;
  park_timer?: number;
  park_idle_timer?: number;
  gps_blindness_timer?: number;
  
  // Battery thresholds
  battery_low_threshold?: number;
  
  // Temperature & Humidity (Tesla-specific fields)
  temp_humi_alarm_enable?: boolean;
  temperature_enabled?: boolean;
  temperature_min?: number;
  temperature_max?: number;
  temp_min?: number;
  temp_max?: number;
  temp_humi_time_gap?: number;
  
  // Humidity thresholds
  humidity_enabled?: boolean;
  humidity_min?: number;
  humidity_max?: number;
  humi_min?: number;
  humi_max?: number;
  
  // Light sensor thresholds (Tesla-specific fields)
  light_alarm_enable?: boolean;
  light_enabled?: boolean;
  light_threshold?: number;
  light_alarm_threshold?: number;
  light_alarm_time_gap?: number;
  light_threshold_low?: number;
  light_threshold_high?: number;
  
  // G-Sensor (Tesla-specific fields)
  g_sensor_alarm_enable?: boolean;
  g_sensor_enabled?: boolean;
  motion_enabled?: boolean;
  g_sensor_threshold?: number;
  g_sensor_time_gap?: number;
  g_sensor_count?: number;
  g_sensor_time?: number;
  g_sensor_range?: string;
  motion_sensitivity?: 'low' | 'medium' | 'high';
  motion_threshold?: number;
  
  // GNSS (GPS) settings (Tesla-specific fields)
  gnss_enable?: boolean;
  gnss_enabled?: boolean;
  gnss_galaxy?: string;
  
  // Speed thresholds
  speed_enabled?: boolean;
  speed_threshold_min?: number;
  speed_threshold_max?: number;
  speed_limit?: number;
  overspeed_duration?: number;
  
  // Pressure thresholds
  pressure_enabled?: boolean;
  pressure_min?: number;
  pressure_max?: number;
  
  // Vibration sensor
  vibration_enabled?: boolean;
  vibration_sensitivity?: 'low' | 'medium' | 'high';
  
  // Alarms & Safety (Tesla-specific fields)
  sms_alarm_enable?: number;
  theft_alarm?: boolean;
  // Note: These fields are not in device_settings table - only in raw_config
  // illegal_ignition_alarm?: number;
  // illegal_movement_distance?: number;
  // alarm_radius?: number;
  // max_continuous_driving?: number;
  
  // Geofence and other settings
  geofence_enabled?: boolean;
  geofence_alerts_enabled?: boolean;
  panic_button_enabled?: boolean;
  sos_enabled?: boolean;
  
  // Device Operation (Tesla-specific fields)
  protocol_type?: number;
  report_mode?: number;
  timer_switch?: number;
  photo_storage?: number;
  
  // Alert settings
  alert_email?: string;
  alert_phone?: string;
  alert_webhook?: string;
  
  // Extended configuration
  additional_config?: Record<string, any>;
  raw_config?: Record<string, any>;
  device_config?: Record<string, any>;
  
  // Additional fields from raw_config
  charging_state?: string;
  
  // Location data
  last_latitude?: number;
  last_longitude?: number;
  
  // Sensor data
  last_temperature?: number;
  last_water_confidence?: number;
  last_speed?: number;
  last_heading?: number;
  battery_percentage?: number;
  cell_signal_strength?: number;
}

export interface Geofence {
  id: string;
  name: string;
  latitude: number;
  longitude: number;
  radius: number;
  active: boolean;
  devices?: string[];
  created_at: string;
  updated_at: string;
}

export class TraqPackAPI {
  private async getMyDeviceIdSet(): Promise<Set<string>> {
    try {
      const { data } = await supabase.rpc('get_user_devices');
      const set = new Set<string>();
      (data || []).forEach((d: any) => {
        if (d?.device_id) {
          const vars = getDeviceIdVariations(d.device_id);
          vars.forEach(v => set.add(v));
        }
      });
      return set;
    } catch {
      return new Set();
    }
  }
  async getGpsData(timeFilter: string = '1h', deviceId?: string): Promise<GpsData[]> {
    console.log('🔍 Getting GPS data with filter:', timeFilter, 'device:', deviceId);
    
    // Prefer secure RPC that filters by customer_id
    try {
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_customer_gps_data', {
          p_time_window: timeFilter,
          p_device_id: deviceId || null
        });
      
      if (rpcError) {
        console.warn('RPC get_customer_gps_data failed, falling back to direct query:', rpcError.message);
        throw rpcError; // fall to catch block to use fallback
      }
      
      if (rpcData && rpcData.length > 0) {
        console.log('📊 RPC GPS Data count:', rpcData.length);
        return rpcData as unknown as GpsData[];
      }
    } catch (e) {
      // Continue to fallback logic below
    }

    // Fallback to direct table query (RLS should still protect access)
    let query = supabase
      .from('gps_data')
      .select('*')
      .order('created_at', { ascending: false });

    if (deviceId) {
      const variations = getDeviceIdVariations(deviceId);
      if (variations.length > 1) {
        query = query.in('device_id', variations);
      } else {
        query = query.eq('device_id', deviceId);
      }
    }

    const now = new Date();
    let fromDate: Date;
    switch (timeFilter) {
      case '1d': fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000); break;
      case '1w': fromDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000); break;
      case '1m': fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000); break;
      default: fromDate = new Date(now.getTime() - 60 * 60 * 1000);
    }
    query = query.gte('created_at', fromDate.toISOString());
    const { data: gpsData, error } = await query.limit(1000);
    if (error) throw error;
    if (!gpsData || gpsData.length === 0) return [];

    // Get device settings to use the correct nicknames
    const deviceIds = Array.from(new Set(gpsData.map(d => d.device_id)));
    const { data: deviceSettings } = await supabase
      .from('device_settings')
      .select('device_id, device_nickname')
      .in('device_id', deviceIds);

    // Create a map of device_id to nickname from device_settings
    const nicknameMap = new Map<string, string>();
    deviceSettings?.forEach(device => {
      if (device.device_nickname) {
        nicknameMap.set(device.device_id, device.device_nickname);
      }
    });

    // Override GPS data nicknames with device_settings nicknames
    const result = gpsData.map(gps => ({
      ...gps,
      device_nickname: nicknameMap.get(gps.device_id) || gps.device_nickname,
      // Extract firmware and ICCID from raw_data if available
      firmware_version: gps.raw_data?.firmware || gps.firmware_version,
      iccid: gps.raw_data?.iccid || gps.iccid
    }));

    // Debug logging for ICCID and firmware
    if (result.length > 0) {
      console.log('🔍 GPS Data after processing:', result.slice(0, 3).map(item => ({
        device_id: item.device_id,
        firmware_version: item.firmware_version,
        iccid: item.iccid,
        raw_data_firmware: item.raw_data?.firmware,
        raw_data_iccid: item.raw_data?.iccid
      })));
    }

    return result;
  }

  async getDevices(): Promise<Device[]> {
    console.log('getDevices called');
    
    // Get all device configs (this is our main source now)
    const { data: deviceConfigs, error: configError } = await supabase
      .from('device_config')
      .select('*');
    
    if (configError) {
      console.error('Error fetching device configs:', configError);
      return [];
    }
    
    console.log('Device configs found:', deviceConfigs?.length || 0);
    
    // Get device settings for nickname and basic info
    const { data: deviceSettings } = await supabase
      .from('device_settings')
      .select('device_id, device_nickname, is_active, device_type, reporting_interval, movement_detection, stationary_interval, temperature_min, temperature_max, speed_limit, battery_low_threshold, geofence_alerts_enabled');
    
    // Helper to normalize to last 12 numeric digits
    const normalize12 = (id?: string) => (id || '').replace(/[^0-9]/g, '').slice(-12);
    // Create settings maps (exact and normalized)
    const settingsMapExact = new Map<string, any>();
    const settingsMapNorm = new Map<string, any>();
    deviceSettings?.forEach(setting => {
      settingsMapExact.set(setting.device_id, setting);
      const n = normalize12(setting.device_id);
      if (n) settingsMapNorm.set(n, setting);
    });
    
    // Deduplicate device_config rows by device_id (keep most recent)
    const uniqueConfigByDeviceId = new Map<string, any>();
    deviceConfigs?.forEach(cfg => {
      const existing = uniqueConfigByDeviceId.get(cfg.device_id);
      if (!existing) {
        uniqueConfigByDeviceId.set(cfg.device_id, cfg);
        return;
      }
      const existingTs = new Date(existing.last_updated || existing.created_at || 0).getTime();
      const currentTs = new Date(cfg.last_updated || cfg.created_at || 0).getTime();
      if (currentTs >= existingTs) {
        uniqueConfigByDeviceId.set(cfg.device_id, cfg);
      }
    });
    const uniqueDeviceConfigs = Array.from(uniqueConfigByDeviceId.values());

    // Get latest GPS data for last_seen
    const { data: gpsData } = await supabase
      .from('gps_data')
      .select('device_id, created_at')
      .order('created_at', { ascending: false });
    
    // Create last seen maps (exact and normalized)
    const lastSeenExact = new Map<string, string>();
    const lastSeenNorm = new Map<string, string>();
    gpsData?.forEach(record => {
      if (!lastSeenExact.has(record.device_id)) {
        lastSeenExact.set(record.device_id, record.created_at);
      }
      const n = normalize12(record.device_id);
      if (n && !lastSeenNorm.has(n)) {
        lastSeenNorm.set(n, record.created_at);
      }
    });
    
    // Build devices from de-duplicated device_config
    const devices: Device[] = uniqueDeviceConfigs.map(config => {
      const n = normalize12(config.device_id);
      const settings = (n && settingsMapNorm.get(n)) || settingsMapExact.get(config.device_id);
      const lastSeen = (n && lastSeenNorm.get(n)) || lastSeenExact.get(config.device_id);
      
      // Parse raw_config if it's a string
      let rawConfigData = null;
      if (config.raw_config) {
        try {
          rawConfigData = typeof config.raw_config === 'string' 
            ? JSON.parse(config.raw_config) 
            : config.raw_config;
        } catch (e) {
          console.error('Error parsing raw_config:', e);
        }
      }
      
      // Parse configuration from raw hex data
      const parsedConfig = parseDeviceRawConfig(rawConfigData);
      
      // Extract firmware and ICCID from raw_config
      const firmware = rawConfigData?.firmware || rawConfigData?.firmware_version;
      const iccid = rawConfigData?.iccid;
      
      // Debug logging for ICCID and firmware
      if (config.device_id === '251075013057') {
        console.log('🔍 Device 251075013057 raw_config:', rawConfigData);
        console.log('🔍 Device 251075013057 firmware:', firmware);
        console.log('🔍 Device 251075013057 iccid:', iccid);
      }
      
      // Determine if device is active (seen in last 24 hours)
      const isActive = lastSeen 
        ? (new Date().getTime() - new Date(lastSeen).getTime()) < 24 * 60 * 60 * 1000
        : false;
      
      return {
        // Basic identification
        id: config.device_id,
        device_id: config.device_id,
        device_nickname: settings?.device_nickname || `Device ${config.device_id}`,
        device_type: settings?.device_type || 'GPS Tracker',
        is_active: settings?.is_active ?? isActive,
        last_seen: lastSeen,
        created_at: config.created_at,
        updated_at: config.last_updated || config.created_at,
        
        // Device identification fields (NEW) - from raw_config
        firmware_version: firmware,
        iccid: iccid,
        last_firmware_update: firmware ? lastSeen : undefined,
        
        // From device_settings (basic thresholds)
        reporting_interval: settings?.reporting_interval,
        movement_detection: settings?.movement_detection,
        stationary_interval: settings?.stationary_interval,
        temperature_min: settings?.temperature_min,
        temperature_max: settings?.temperature_max,
        speed_limit: settings?.speed_limit,
        battery_low_threshold: settings?.battery_low_threshold || config.low_battery_threshold,
        geofence_alerts_enabled: settings?.geofence_alerts_enabled || config.geo_fence_enabled,
        
        // From device_config
        server_ip: config.server_ip,
        server_port: config.server_port,
        report_interval: config.report_interval,
        temperature_threshold: config.temperature_threshold,
        humidity_threshold: config.humidity_threshold,
        light_threshold: config.light_threshold,
        movement_threshold: config.movement_threshold,
        pressure_threshold: config.pressure_threshold,
        battery_alert_enabled: config.battery_alert_enabled,
        geo_fence_enabled: config.geo_fence_enabled,
        motion_alarm_enabled: config.motion_alarm_enabled,
        temperature_alarm_enabled: config.temperature_alarm_enabled,
        
        // From parsed raw_config
        ...parsedConfig,
        
        // Ensure critical fields from parsed config
        apn: parsedConfig.apn || config.apn,
        main_server: parsedConfig.main_server || config.server_ip,
        main_server_port: parsedConfig.main_server_port || config.server_port,
        
        // Store raw config for debugging
        raw_config: config.raw_config
      };
    }) || [];
    
    console.log('Final devices:', devices.length);
    if (devices.length > 0) {
      console.log('Sample device config:', {
        device_id: devices[0].device_id,
        nickname: devices[0].device_nickname,
        apn: devices[0].apn,
        main_server: devices[0].main_server,
        main_server_port: devices[0].main_server_port,
        temp_humi_alarm_enable: devices[0].temp_humi_alarm_enable,
        light_alarm_enable: devices[0].light_alarm_enable,
        g_sensor_alarm_enable: devices[0].g_sensor_alarm_enable
      });
      
      // Debug logging for specific device
      const motiDevice = devices.find(d => d.device_id === '251075013057');
      if (motiDevice) {
        console.log('🔍 Moti Device final object:', {
          device_id: motiDevice.device_id,
          iccid: motiDevice.iccid,
          firmware_version: motiDevice.firmware_version
        });
      }
    }
    
    return devices;
  }

  private async getDevicesFromGpsData(): Promise<Device[]> {
    // Fallback method to derive devices from GPS data
    const { data, error } = await supabase
      .from('gps_data')
      .select('device_id, device_nickname, created_at, battery_percentage, raw_data')
      .order('created_at', { ascending: false })
      .limit(1000);

    if (error) {
      console.error('Error fetching GPS data for devices:', error);
      return [];
    }
    
    console.log('GPS data records found:', data?.length || 0);
    if (data && data.length > 0) {
      const uniqueDeviceIds = Array.from(new Set(data.map(d => d.device_id)));
      console.log('Unique device IDs:', uniqueDeviceIds);
      console.log('Sample records:', data.slice(0, 5));
    }

    // Group by device_id and get latest info
    const deviceMap = new Map<string, Device>();
    const deviceIdsToCreate: string[] = [];
    
    data?.forEach((record) => {
      if (!deviceMap.has(record.device_id)) {
        const timeDiff = new Date().getTime() - new Date(record.created_at).getTime();
        const isActive = timeDiff < 24 * 60 * 60 * 1000; // 24 hours
        
        // Use GPS nickname or default
        const deviceNickname = record.device_nickname || `Device ${record.device_id}`;
        
        deviceMap.set(record.device_id, {
          id: record.device_id,
          device_id: record.device_id,
          device_nickname: deviceNickname,
          device_type: 'GPS Tracker',
          is_active: isActive,
          last_seen: record.created_at,
          // Only include fields we know exist or are client-side only
        });
        
        deviceIdsToCreate.push(record.device_id);
      }
    });

    // We'll handle device creation in getDevices() instead
    
    const devices = Array.from(deviceMap.values());
    console.log('Devices found from GPS data:', devices.length, devices);
    return devices;
  }

  async getAlerts(excludeGeofenceAlerts: boolean = false): Promise<Alert[]> {
    try {
      // Build query
      let query = supabase
        .from('alerts')
        .select('*');
      
      // Exclude geofence alerts if requested (for super admin)
      if (excludeGeofenceAlerts) {
        query = query.not('alert_type', 'in', '(geofence,geofence_entry,geofence_exit)');
      }
      
      // Apply ordering and limit
      query = query
        .order('created_at', { ascending: false })
        .limit(50);

      const { data: alertsData, error } = await query;

      if (error) {
        console.error('Error loading alerts:', error);
        return [];
      }

      // Transform database alerts to frontend format
      const alerts: Alert[] = (alertsData || []).map((alert: any) => ({
        id: alert.id,
        device_id: alert.device_id,
        type: alert.alert_type || 'general',
        message: alert.message,
        severity: alert.severity || 'medium',
        timestamp: alert.created_at,
        acknowledged: alert.is_read || false,
        title: alert.title
      }));

      return alerts;
    } catch (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }
  }

  subscribeToGpsData(callback: (payload: any) => void) {
    return supabase
      .channel('gps_data_changes')
      .on('postgres_changes', 
        { event: 'INSERT', schema: 'public', table: 'gps_data' },
        callback
      )
      .subscribe();
  }

  async getStats(): Promise<{
    totalDevices: number;
    activeDevices: number;
    totalReadings: number;
    recentReadings: number;
  }> {
    const devices = await this.getDevices();
    const activeDevices = devices.filter(d => d.is_active).length;
    
    const { count: totalReadings } = await supabase
      .from('gps_data')
      .select('*', { count: 'exact', head: true });

    const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const { count: recentReadings } = await supabase
      .from('gps_data')
      .select('*', { count: 'exact', head: true })
      .gte('created_at', yesterday.toISOString());

    return {
      totalDevices: devices.length,
      activeDevices,
      totalReadings: totalReadings || 0,
      recentReadings: recentReadings || 0,
    };
  }

  async updateDevice(deviceId: string, name: string): Promise<void> {
    console.log('Updating device nickname via RPC:', deviceId, name);
    // Preferred path: secure RPC with multi‑tenant checks and device ID normalization
    const { error: rpcError } = await supabase.rpc('update_device_nickname', {
      p_device_id: deviceId,
      p_new_name: name,
    });
    if (!rpcError) {
      console.log('Device nickname saved successfully via RPC');
      return;
    }
    console.warn('update_device_nickname RPC failed, attempting fallback update:', rpcError.message);

    // Fallback (legacy): try direct update using known device ID variations.
    // Note: May still be blocked by RLS; kept only as a best-effort for super admins.
    const variations = getDeviceIdVariations(deviceId);
    const { error: updateError } = await supabase
      .from('device_settings')
      .update({ device_nickname: name })
      .in('device_id', variations);
    if (updateError) {
      console.error('Fallback update failed:', updateError);
      throw rpcError; // surface the original RPC error (authoritative path)
    }
    console.log('Device nickname saved via fallback update');
  }

  async updateDeviceConfig(deviceId: string, updates: Partial<Device>): Promise<void> {
    console.log('Updating device configuration:', deviceId, updates);
    
    // First check if device exists in device_settings
    const { data: existing, error: checkError } = await supabase
      .from('device_settings')
      .select('*')
      .eq('device_id', deviceId)
      .single();
    
    if (checkError || !existing) {
      // Device doesn't exist, create it with updates
      console.log('Device not in device_settings, creating entry with config');
      const { error: insertError } = await supabase
        .from('device_settings')
        .insert({
          device_id: deviceId,
          ...updates,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (insertError) {
        console.error('Error creating device:', insertError);
        throw insertError;
      }
    } else {
      // Device exists, update it
      console.log('Device exists, updating configuration');
      const { error: updateError } = await supabase
        .from('device_settings')
        .update({ 
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('device_id', deviceId);

      if (updateError) {
        console.error('Error updating device configuration:', updateError);
        throw updateError;
      }
    }
    
    console.log('Device configuration saved successfully');
  }

  async createDevice(deviceData: Partial<Device>): Promise<Device | null> {
    if (!deviceData.device_id) {
      console.error('Device ID (IMEI) is required');
      return null;
    }

    // Check if device with this IMEI already exists
    const { data: existing } = await supabase
      .from('device_settings')
      .select('*')
      .eq('device_id', deviceData.device_id)
      .single();

    if (existing) {
      console.log(`Device with IMEI ${deviceData.device_id} already exists`);
      return existing; // Return existing device instead of creating duplicate
    }

    // Create new device with minimal fields for compatibility
    const newDevice = {
      device_id: deviceData.device_id,
      device_nickname: deviceData.device_nickname || `Device ${deviceData.device_id}`,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const { data, error } = await supabase
      .from('device_settings')
      .insert(newDevice)
      .single();

    if (error) {
      console.error('Error creating device:', error);
      return null;
    }

    return data;
  }

  private async ensureDeviceExists(deviceId: string): Promise<void> {
    // Try to create a minimal device entry if it doesn't exist
    try {
      const { error } = await supabase
        .from('device_settings')
        .insert({
          device_id: deviceId,
          device_nickname: `Device ${deviceId}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      
      if (!error) {
        console.log(`Created device settings entry for ${deviceId}`);
      }
    } catch (e) {
      // Ignore errors - device might already exist
      console.log(`Device ${deviceId} might already exist`);
    }
  }

  async queryWithAI(query: string, conversationHistory?: any[], isFollowUp?: boolean): Promise<{
    response: string;
    data: GpsData[];
    intent: any;
  }> {
    // Handle common local intents deterministically before calling Edge Function

    // Global guard: if query mentions a specific IMEI, ensure it belongs to caller's customer
    try {
      const imeiInQuery = (query.match(/\d{10,15}/g) || [])[0];
      if (imeiInQuery) {
        const allowed = await this.getMyDeviceIdSet();
        const owns = getDeviceIdVariations(imeiInQuery).some(v => allowed.has(v));
        if (!owns) {
          return {
            response: `The device ${imeiInQuery} is not part of your organization.`,
            data: [],
            intent: { type: 'not_owned', device_id: imeiInQuery }
          };
        }
      }
    } catch {}
    // 0) Battery level at a specific time (prioritize before generic handlers)
    const batteryYesterdayAt = /battery\s*(level|percentage)?/i.test(query) && query.match(/yesterday\s+at\s+((?:noon|midnight)|(?:\d{1,2})(?::(\d{2}))?\s*(am|pm)?)/i);
    if (batteryYesterdayAt) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , timeToken, mmStrMaybe, apMaybe ] = batteryYesterdayAt;
          const to24 = (token: string, mmStr?: string, ap?: string) => {
            if (/^noon$/i.test(token)) return { h: '12', m: '00' };
            if (/^midnight$/i.test(token)) return { h: '00', m: '00' };
            let h = Math.max(0, Math.min(23, parseInt(token || '0')));
            let m = Math.max(0, Math.min(59, parseInt(mmStr || '0')));
            if (ap) {
              const apLower = ap.toLowerCase();
              if (apLower === 'pm' && h < 12) h += 12;
              if (apLower === 'am' && h === 12) h = 0;
            }
            return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
          };
          const t = to24(timeToken, mmStrMaybe, apMaybe);
          const ymd = getYesterdayYmd(timeZone);
          const targetLocal = `${ymd}T${t.h}:${t.m}:00`;
          const targetUtc = localTimeInZoneToUtcIso(targetLocal, timeZone);
          const dayRange = parseExplicitDateRange(ymd, timeZone)!;
          const variations = getDeviceIdVariations(imeiMatch);
          let q = supabase
            .from('gps_data')
            .select('*')
            .gte('created_at', dayRange.start)
            .lte('created_at', dayRange.end)
            .order('created_at', { ascending: true })
            .limit(1000);
          q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
          const { data, error } = await q;
          if (error) throw error;
          const points = (data || []) as unknown as GpsData[];
          if (points.length === 0) {
            return { response: `No battery data for device ${imeiMatch} yesterday.`, data: [], intent: { type: 'battery_at_time', device_id: imeiMatch, ymd, time: targetUtc, found: false } };
          }
          const targetMs = Date.parse(targetUtc);
          let before: GpsData | null = null;
          let after: GpsData | null = null;
          for (const p of points) {
            const ms = Date.parse(p.created_at);
            if (ms <= targetMs) before = p;
            if (ms >= targetMs) { after = p; break; }
          }
          const exact = points.find(p => Math.abs(Date.parse(p.created_at) - targetMs) <= 60 * 1000);
          if (exact && typeof exact.battery_percentage === 'number') {
            const ts = new Date(exact.created_at).toLocaleString();
            return { response: `🔋 Battery level at the requested time was ${Math.round(exact.battery_percentage)}% at ${ts}.`, data: [exact], intent: { type: 'battery_at_time', device_id: imeiMatch, exact: true } };
          }
          const parts: string[] = [];
          const human = /noon/i.test(timeToken) ? '12:00 PM' : /midnight/i.test(timeToken) ? '12:00 AM' : `${String(parseInt(timeToken)).padStart(2,'0')}:${(mmStrMaybe||'00').padStart(2,'0')}${apMaybe?` ${apMaybe}`:''}`;
          parts.push(`🔋 No reading exactly at ${human} yesterday.`);
          if (before && typeof before.battery_percentage === 'number') {
            parts.push(`Nearest BEFORE: ${Math.round(before.battery_percentage)}% at ${new Date(before.created_at).toLocaleString()}.`);
          }
          if (after && typeof after.battery_percentage === 'number') {
            parts.push(`Nearest AFTER: ${Math.round(after.battery_percentage)}% at ${new Date(after.created_at).toLocaleString()}.`);
          }
          return { response: parts.join(' '), data: [before, after].filter(Boolean) as any, intent: { type: 'battery_at_time', device_id: imeiMatch, exact: false } };
        }
      } catch {}
    }

    // 0.a) Battery level today (latest today or nearest before/after)
    const batteryToday = /battery\s*(level|percentage)?/i.test(query) && /\btoday\b/i.test(query);
    if (batteryToday) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const ymd = getTodayYmd(timeZone);
          const range = parseExplicitDateRange(ymd, timeZone)!;
          const variations = getDeviceIdVariations(imeiMatch);
          const base = supabase.from('gps_data').select('*');
          let q = base
            .gte('created_at', range.start)
            .lte('created_at', range.end)
            .order('created_at', { ascending: false })
            .limit(1);
          q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
          const { data, error } = await q;
          if (error) throw error;
          const points = (data || []) as unknown as GpsData[];
          if (points.length > 0 && typeof points[0].battery_percentage === 'number') {
            const p = points[0];
            const ts = new Date(p.created_at).toLocaleString();
            return { response: `🔋 Battery level today is ${Math.round(p.battery_percentage)}% at ${ts}.`, data: [p], intent: { type: 'battery_today', device_id: imeiMatch } };
          }
          // Nearest before/after if no reading today
          let before: GpsData | null = null;
          let after: GpsData | null = null;
          {
            let qb = base.lt('created_at', range.start).order('created_at', { ascending: false }).limit(1);
            qb = variations.length > 1 ? qb.in('device_id', variations) : qb.eq('device_id', imeiMatch);
            const { data: b } = await qb as any;
            before = (b && b[0]) || null;
          }
          {
            let qa = base.gt('created_at', range.end).order('created_at', { ascending: true }).limit(1);
            qa = variations.length > 1 ? qa.in('device_id', variations) : qa.eq('device_id', imeiMatch);
            const { data: a } = await qa as any;
            after = (a && a[0]) || null;
          }
          const parts: string[] = [];
          parts.push('🔋 No battery reading found today.');
          if (before && typeof before.battery_percentage === 'number') {
            parts.push(`Nearest BEFORE: ${Math.round(before.battery_percentage)}% at ${new Date(before.created_at).toLocaleString()}.`);
          }
          if (after && typeof after.battery_percentage === 'number') {
            parts.push(`Nearest AFTER: ${Math.round(after.battery_percentage)}% at ${new Date(after.created_at).toLocaleString()}.`);
          }
          return { response: parts.join(' '), data: [before, after].filter(Boolean) as any, intent: { type: 'battery_today', device_id: imeiMatch, found: false } };
        }
      } catch {}
    }

    const batteryOnDateAt = /battery\s*(level|percentage)?/i.test(query) && query.match(/on\s+((?:20\d{2})[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:20\d{2}))\s+at\s+((?:noon|midnight)|(?:\d{1,2})(?::(\d{2}))?\s*(am|pm)?)/i);
    if (batteryOnDateAt) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , dateText, timeToken, mmStrMaybe, apMaybe ] = batteryOnDateAt;
          const ymd = parseDateTextToYmd(dateText);
          if (ymd) {
            const to24 = (token: string, mmStr?: string, ap?: string) => {
              if (/^noon$/i.test(token)) return { h: '12', m: '00' };
              if (/^midnight$/i.test(token)) return { h: '00', m: '00' };
              let h = Math.max(0, Math.min(23, parseInt(token || '0')));
              let m = Math.max(0, Math.min(59, parseInt(mmStr || '0')));
              if (ap) {
                const l = ap.toLowerCase();
                if (l === 'pm' && h < 12) h += 12;
                if (l === 'am' && h === 12) h = 0;
              }
              return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
            };
            const t = to24(timeToken, mmStrMaybe, apMaybe);
            const targetLocal = `${ymd}T${t.h}:${t.m}:00`;
            const targetUtc = localTimeInZoneToUtcIso(targetLocal, timeZone);
            const dayRange = parseExplicitDateRange(ymd, timeZone)!;
            const variations = getDeviceIdVariations(imeiMatch);
            let q = supabase
              .from('gps_data')
              .select('*')
              .gte('created_at', dayRange.start)
              .lte('created_at', dayRange.end)
              .order('created_at', { ascending: true })
              .limit(1000);
            q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
            const { data, error } = await q;
            if (error) throw error;
            const points = (data || []) as unknown as GpsData[];
            if (points.length === 0) {
              return { response: `No battery data for device ${imeiMatch} on ${dateText.replace(/[/.]/g,'-')}.`, data: [], intent: { type: 'battery_at_time_on_date', device_id: imeiMatch, ymd, time: targetUtc, found: false } };
            }
            const targetMs = Date.parse(targetUtc);
            let before: GpsData | null = null;
            let after: GpsData | null = null;
            for (const p of points) {
              const ms = Date.parse(p.created_at);
              if (ms <= targetMs) before = p;
              if (ms >= targetMs) { after = p; break; }
            }
            const exact = points.find(p => Math.abs(Date.parse(p.created_at) - targetMs) <= 60 * 1000);
            if (exact && typeof exact.battery_percentage === 'number') {
              const ts = new Date(exact.created_at).toLocaleString();
              return { response: `🔋 Battery level at the requested time was ${Math.round(exact.battery_percentage)}% at ${ts}.`, data: [exact], intent: { type: 'battery_at_time_on_date', device_id: imeiMatch, exact: true } };
            }
            const parts: string[] = [];
            const human = /noon/i.test(timeToken) ? '12:00 PM' : /midnight/i.test(timeToken) ? '12:00 AM' : `${String(parseInt(timeToken)).padStart(2,'0')}:${(mmStrMaybe||'00').padStart(2,'0')}${apMaybe?` ${apMaybe}`:''}`;
            parts.push(`🔋 No reading exactly at ${human} on ${dateText.replace(/[/.]/g,'-')}.`);
            if (before && typeof before.battery_percentage === 'number') {
              parts.push(`Nearest BEFORE: ${Math.round(before.battery_percentage)}% at ${new Date(before.created_at).toLocaleString()}.`);
            }
            if (after && typeof after.battery_percentage === 'number') {
              parts.push(`Nearest AFTER: ${Math.round(after.battery_percentage)}% at ${new Date(after.created_at).toLocaleString()}.`);
            }
            return { response: parts.join(' '), data: [before, after].filter(Boolean) as any, intent: { type: 'battery_at_time_on_date', device_id: imeiMatch, exact: false } };
          }
        }
      } catch {}
    }

    // 0.b) Distance traveled yesterday (in km)
    const distanceYesterday = /(how\s+many\s+(kilometers|km)|distance|travel)/i.test(query) && /yesterday/i.test(query);
    if (distanceYesterday) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'distance_yesterday', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const ymd = getYesterdayYmd(timeZone);
          const dayRange = parseExplicitDateRange(ymd, timeZone)!;
          const variations = getDeviceIdVariations(imeiMatch);
          let q = supabase
            .from('gps_data')
            .select('device_id, latitude, longitude, created_at')
            .gte('created_at', dayRange.start)
            .lte('created_at', dayRange.end)
            .order('created_at', { ascending: true })
            .limit(5000);
          q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
          const { data, error } = await q;
          if (error) throw error;
          const points = (data || []) as unknown as GpsData[];
          if (points.length < 2) {
            return { response: `Not enough data to calculate distance for device ${imeiMatch} yesterday.`, data: [], intent: { type: 'distance_yesterday', device_id: imeiMatch, points: points.length } };
          }
          let km = 0;
          for (let i = 1; i < points.length; i++) {
            const a = points[i - 1];
            const b = points[i];
            if (typeof a.latitude === 'number' && typeof a.longitude === 'number' && typeof b.latitude === 'number' && typeof b.longitude === 'number') {
              km += haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
            }
          }
          const rounded = Math.round(km * 100) / 100;
          return { response: `Device ${imeiMatch} traveled ${rounded} km yesterday.`, data: points as any, intent: { type: 'distance_yesterday', device_id: imeiMatch, km: rounded } };
        }
      } catch {}
    }

    // 0.c) Distance traveled on DATE (in km)
    const distanceOnDateMatch = query.match(/(?:how\s+many\s+(?:kilometers|km)|distance|travel)[^\d]*(?:on)\s+((?:20\d{2})[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:20\d{2}))/i);
    if (distanceOnDateMatch) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'distance_on_date', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const dateText = distanceOnDateMatch[1];
          const ymd = parseDateTextToYmd(dateText);
          if (ymd) {
            const dayRange = parseExplicitDateRange(ymd, timeZone)!;
            const variations = getDeviceIdVariations(imeiMatch);
            let q = supabase
              .from('gps_data')
              .select('device_id, latitude, longitude, created_at')
              .gte('created_at', dayRange.start)
              .lte('created_at', dayRange.end)
              .order('created_at', { ascending: true })
              .limit(5000);
            q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
            const { data, error } = await q;
            if (error) throw error;
            const points = (data || []) as unknown as GpsData[];
            if (points.length < 2) {
              return { response: `Not enough data to calculate distance for device ${imeiMatch} on ${dateText.replace(/[/.]/g,'-')}.`, data: [], intent: { type: 'distance_on_date', device_id: imeiMatch, points: points.length } };
            }
            let km = 0;
            for (let i = 1; i < points.length; i++) {
              const a = points[i - 1];
              const b = points[i];
              if (typeof a.latitude === 'number' && typeof a.longitude === 'number' && typeof b.latitude === 'number' && typeof b.longitude === 'number') {
                km += haversineKm(a.latitude, a.longitude, b.latitude, b.longitude);
              }
            }
            const rounded = Math.round(km * 100) / 100;
            return { response: `Device ${imeiMatch} traveled ${rounded} km on ${dateText.replace(/[/.]/g,'-')}.`, data: points as any, intent: { type: 'distance_on_date', device_id: imeiMatch, km: rounded, date: ymd } };
          }
        }
      } catch {}
    }

    // 0) Relative time window with explicit date: "between HH[:MM] and HH[:MM] on DD-MM-YYYY or YYYY-MM-DD"
    const betweenOnDate = query.match(/between\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+and\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+on\s+((?:20\d{2})[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:20\d{2}))/i);
    if (betweenOnDate) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check first
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , sh, sm, sampm, eh, em, eampm, dateText ] = betweenOnDate;
          const to24 = (hStr?: string, mStr?: string, ap?: string) => {
            let h = Math.max(0, Math.min(23, parseInt(hStr || '0')));
            let m = Math.max(0, Math.min(59, parseInt(mStr || '0')));
            if (ap) {
              const apLower = ap.toLowerCase();
              if (apLower === 'pm' && h < 12) h += 12;
              if (apLower === 'am' && h === 12) h = 0;
            }
            return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
          };
          const s = to24(sh, sm, sampm);
          const e = to24(eh, em, eampm);
          const ymd = parseDateTextToYmd(dateText);
          if (ymd) {
            const startLocal = `${ymd}T${s.h}:${s.m}:00`;
            const endLocal = `${ymd}T${e.h}:${e.m}:59`;
            const startUtc = localTimeInZoneToUtcIso(startLocal, timeZone);
            const endUtc = localTimeInZoneToUtcIso(endLocal, timeZone);
            const variations = getDeviceIdVariations(imeiMatch);
            let q = supabase
              .from('gps_data')
              .select('*')
              .gte('created_at', startUtc)
              .lte('created_at', endUtc)
              .order('created_at', { ascending: true })
              .limit(1000);
            if (variations.length > 1) q = q.in('device_id', variations); else q = q.eq('device_id', imeiMatch);
            const { data, error } = await q;
            if (error) throw error;
            const points = (data || []) as unknown as GpsData[];
            if (points.length > 0) {
              const first = points[0];
              const last = points[points.length - 1];
              const resp = `Found ${points.length} locations for device ${imeiMatch} between ${sh}${sm?':'+sm:''}${sampm?(' '+sampm):''} and ${eh}${em?':'+em:''}${eampm?(' '+eampm):''} on ${dateText.replace(/[/.]/g, '-')}. First at ${first.latitude.toFixed(5)}, ${first.longitude.toFixed(5)} at ${new Date(first.created_at).toLocaleString()} — last at ${last.latitude.toFixed(5)}, ${last.longitude.toFixed(5)} at ${new Date(last.created_at).toLocaleString()}.`;
              return { response: resp, data: points, intent: { type: 'history_by_time_range_on_date', device_id: imeiMatch, date: ymd, start: startUtc, end: endUtc } };
            } else {
              return { response: `No locations found for device ${imeiMatch} between ${sh}${sm?':'+sm:''}${sampm?(' '+sampm):''} and ${eh}${em?':'+em:''}${eampm?(' '+eampm):''} on ${dateText.replace(/[/.]/g, '-')}.`, data: [], intent: { type: 'history_by_time_range_on_date', device_id: imeiMatch, date: ymd, start: startUtc, end: endUtc, found: false } };
            }
          }
        }
      } catch {}
    }
    // 0.2) Explicit date queries like: "Where was device 251075013057 on 2025-09-16" or "on 16-09-2025"
    const explicitDateMatch = query.match(/\b(20\d{2})[-/.](0[1-9]|1[0-2])[-/.](0[1-9]|[12]\d|3[01])\b|\b(0[1-9]|[12]\d|3[01])[-/.](0[1-9]|1[0-2])[-/.](20\d{2})\b/);
    // If the query is an "enter city" question, skip the generic explicit-date handler
    if (explicitDateMatch && !/\benter\b/i.test(query)) {
      try {
        // Extract device id (IMEI) if present
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (!imeiMatch) {
          // Fall through to other handlers if no device mentioned
        } else {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const range = parseExplicitDateRange(explicitDateMatch[0], timeZone);
          if (range) {
            const variations = getDeviceIdVariations(imeiMatch);
            let q = supabase
              .from('gps_data')
              .select('*')
              .gte('created_at', range.start)
              .lte('created_at', range.end)
              .order('created_at', { ascending: true })
              .limit(1000);
            if (variations.length > 1) q = q.in('device_id', variations); else q = q.eq('device_id', imeiMatch);
            const { data, error } = await q;
            if (error) throw error;
            const points = (data || []) as unknown as GpsData[];
            if (points.length > 0) {
              const first = points[0];
              const last = points[points.length - 1];
              const dateLabel = explicitDateMatch[0].replace(/[/.]/g, '-');
              const addr = (first as any).address || '';
              const resp = `Found ${points.length} locations for device ${imeiMatch} on ${dateLabel}. First at ${first.latitude.toFixed(5)}, ${first.longitude.toFixed(5)}${addr ? ' ('+addr+')' : ''} at ${new Date(first.created_at).toLocaleString()} — last at ${last.latitude.toFixed(5)}, ${last.longitude.toFixed(5)} at ${new Date(last.created_at).toLocaleString()}.`;
              return { response: resp, data: points, intent: { type: 'history_by_date', device_id: imeiMatch, date: dateLabel } };
            }
          }
        }
      } catch (e) {
        // Continue to other handlers/edge function on error
      }
    }

    // 0.5) Geospatial city-enter intent: "Did device <IMEI> enter Tel Aviv yesterday/on <date>?"
    const enterCityYesterday = query.match(/did\s+device\s+(\d{10,15})\s+enter\s+(.+?)\s+yesterday\b/i);
    const enterCityOnDate = query.match(/did\s+device\s+(\d{10,15})\s+enter\s+(.+?)\s+on\s+((?:20\d{2})[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:20\d{2}))\b/i);
    if (enterCityYesterday || enterCityOnDate) {
      try {
        const imei = (enterCityYesterday || enterCityOnDate)![1];
        const cityRaw = (enterCityYesterday || enterCityOnDate)![2];
        const city = cityRaw.trim().toLowerCase();
        const timeZone = 'Asia/Jerusalem';
        // Ownership check
        const allowed = await this.getMyDeviceIdSet();
        const own = getDeviceIdVariations(imei).some(v => allowed.has(v));
        if (!own) {
          return { response: `The device ${imei} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imei } };
        }
        // Resolve date window
        let range: { start: string; end: string } | null = null;
        let dateLabel = 'yesterday';
        if (enterCityOnDate) {
          const dateText = enterCityOnDate[3];
          const ymd = parseDateTextToYmd(dateText);
          if (ymd) {
            range = parseExplicitDateRange(ymd, timeZone);
            dateLabel = dateText.replace(/[/.]/g, '-');
          }
        } else {
          const ymd = getYesterdayYmd(timeZone);
          range = parseExplicitDateRange(ymd, timeZone);
          dateLabel = 'yesterday';
        }
        if (!range) throw new Error('Invalid date');
        const variations = getDeviceIdVariations(imei);
        let q = supabase
          .from('gps_data')
          .select('*')
          .gte('created_at', range.start)
          .lte('created_at', range.end)
          .order('created_at', { ascending: true })
          .limit(1000);
        q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imei);
        const { data, error } = await q;
        if (error) throw error;
        const points = (data || []) as unknown as GpsData[];

        // Only implement Tel Aviv locally; other cities fall back to Edge
        const isTelAvivQuery = TEL_AVIV_SYNONYMS.some(s => city.includes(s));
        if (isTelAvivQuery) {
          let entered = false;
          let firstEntry: GpsData | null = null;
          for (const p of points) {
            const inside = isInTelAviv(p.latitude, p.longitude, (p as any).address);
            if (inside) {
              entered = true;
              firstEntry = p;
              break;
            }
          }
          if (entered && firstEntry) {
            const ts = new Date(firstEntry.created_at).toLocaleString();
            const addr = (firstEntry as any).address || '';
            const msg = `Yes. Device ${imei} entered Tel Aviv ${dateLabel}. First recorded point inside the city at ${firstEntry.latitude.toFixed(5)}, ${firstEntry.longitude.toFixed(5)}${addr?` (${addr})`:''} at ${ts}.`;
            return { response: msg, data: [firstEntry], intent: { type: 'city_entry', device_id: imei, city: 'Tel Aviv', date: dateLabel, entered: true } };
          }
          return { response: `No. Device ${imei} did not enter Tel Aviv ${dateLabel}.`, data: [], intent: { type: 'city_entry', device_id: imei, city: 'Tel Aviv', date: dateLabel, entered: false } };
        }
      } catch (e) {
        // fall through to edge function
      }
    }
    // 0.3) "yesterday at HH[:MM] [am/pm]?" → find exact or nearest before/after within that day
    const atYesterday = query.match(/yesterday\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (atYesterday) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , hhStr, mmStr, ap ] = atYesterday;
          const to24 = (hStr?: string, mStr?: string, apm?: string) => {
            let h = Math.max(0, Math.min(23, parseInt(hStr || '0')));
            let m = Math.max(0, Math.min(59, parseInt(mStr || '0')));
            if (apm) {
              const l = apm.toLowerCase();
              if (l === 'pm' && h < 12) h += 12;
              if (l === 'am' && h === 12) h = 0;
            }
            return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
          };
          const t = to24(hhStr, mmStr, ap);
          const ymd = getYesterdayYmd(timeZone);
          const targetLocal = `${ymd}T${t.h}:${t.m}:00`;
          const targetUtc = localTimeInZoneToUtcIso(targetLocal, timeZone);
          // Query day range
          const dayRange = parseExplicitDateRange(ymd, timeZone)!;
          const variations = getDeviceIdVariations(imeiMatch);
          let q = supabase
            .from('gps_data')
            .select('*')
            .gte('created_at', dayRange.start)
            .lte('created_at', dayRange.end)
            .order('created_at', { ascending: true })
            .limit(1000);
          q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
          const { data, error } = await q;
          if (error) throw error;
          const points = (data || []) as unknown as GpsData[];
          if (points.length === 0) {
            return { response: `No location data found for device ${imeiMatch} on yesterday.`, data: [], intent: { type: 'point_at_time', device_id: imeiMatch, ymd, time: targetUtc, found: false } };
          }
          // Find nearest before/after
          const targetMs = Date.parse(targetUtc);
          let before: GpsData | null = null;
          let after: GpsData | null = null;
          for (const p of points) {
            const ms = Date.parse(p.created_at);
            if (ms <= targetMs) before = p; // increasing order keeps last <= target
            if (ms >= targetMs) { after = p; break; }
          }
          // Any point exactly at time?
          const exact = points.find(p => Math.abs(Date.parse(p.created_at) - targetMs) <= 60 * 1000);
          if (exact) {
            const ts = new Date(exact.created_at).toLocaleString();
            const addr = (exact as any).address || '';
            return { response: `At the requested time, device ${imeiMatch} was at ${exact.latitude.toFixed(5)}, ${exact.longitude.toFixed(5)}${addr?` (${addr})`:''} at ${ts}.`, data: [exact], intent: { type: 'point_at_time', device_id: imeiMatch, ymd, exact: true } };
          }
          // Build helpful message
          const parts: string[] = [];
          const hhmm = `${hhStr.padStart(2,'0')}:${(mmStr||'00').padStart(2,'0')}` + (ap ? ` ${ap}` : '');
          parts.push(`No reading exactly at ${hhmm} yesterday for device ${imeiMatch}.`);
          if (before) {
            parts.push(`Nearest BEFORE: ${before.latitude.toFixed(5)}, ${before.longitude.toFixed(5)} at ${new Date(before.created_at).toLocaleString()}`);
          }
          if (after) {
            parts.push(`Nearest AFTER: ${after.latitude.toFixed(5)}, ${after.longitude.toFixed(5)} at ${new Date(after.created_at).toLocaleString()}`);
          }
          return { response: parts.join(' '), data: [before, after].filter(Boolean) as any, intent: { type: 'point_at_time', device_id: imeiMatch, ymd, exact: false } };
        }
      } catch {}
    }

    // 0.4) "on <DATE> at HH[:MM] [am/pm]?" → exact or nearest before/after
    const atOnDate = query.match(/on\s+((?:20\d{2})[-/.](?:0?[1-9]|1[0-2])[-/.](?:0?[1-9]|[12]\d|3[01])|(?:0?[1-9]|[12]\d|3[01])[-/.](?:0?[1-9]|1[0-2])[-/.](?:20\d{2}))\s+at\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
    if (atOnDate) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , dateText, hhStr, mmStr, ap ] = atOnDate;
          const ymd = parseDateTextToYmd(dateText);
          if (ymd) {
            const to24 = (hStr?: string, mStr?: string, apm?: string) => {
              let h = Math.max(0, Math.min(23, parseInt(hStr || '0')));
              let m = Math.max(0, Math.min(59, parseInt(mStr || '0')));
              if (apm) {
                const l = apm.toLowerCase();
                if (l === 'pm' && h < 12) h += 12;
                if (l === 'am' && h === 12) h = 0;
              }
              return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
            };
            const t = to24(hhStr, mmStr, ap);
            const targetLocal = `${ymd}T${t.h}:${t.m}:00`;
            const targetUtc = localTimeInZoneToUtcIso(targetLocal, timeZone);
            const dayRange = parseExplicitDateRange(ymd, timeZone)!;
            const variations = getDeviceIdVariations(imeiMatch);
            let q = supabase
              .from('gps_data')
              .select('*')
              .gte('created_at', dayRange.start)
              .lte('created_at', dayRange.end)
              .order('created_at', { ascending: true })
              .limit(1000);
            q = variations.length > 1 ? q.in('device_id', variations) : q.eq('device_id', imeiMatch);
            const { data, error } = await q;
            if (error) throw error;
            const points = (data || []) as unknown as GpsData[];
            if (points.length === 0) {
              return { response: `No location data found for device ${imeiMatch} on ${dateText.replace(/[/.]/g,'-')}.`, data: [], intent: { type: 'point_at_time_on_date', device_id: imeiMatch, ymd, time: targetUtc, found: false } };
            }
            const targetMs = Date.parse(targetUtc);
            let before: GpsData | null = null;
            let after: GpsData | null = null;
            for (const p of points) {
              const ms = Date.parse(p.created_at);
              if (ms <= targetMs) before = p;
              if (ms >= targetMs) { after = p; break; }
            }
            const exact = points.find(p => Math.abs(Date.parse(p.created_at) - targetMs) <= 60 * 1000);
            if (exact) {
              const ts = new Date(exact.created_at).toLocaleString();
              const addr = (exact as any).address || '';
              return { response: `At the requested time, device ${imeiMatch} was at ${exact.latitude.toFixed(5)}, ${exact.longitude.toFixed(5)}${addr?` (${addr})`:''} at ${ts}.`, data: [exact], intent: { type: 'point_at_time_on_date', device_id: imeiMatch, ymd, exact: true } };
            }
            const hhmm = `${hhStr.padStart(2,'0')}:${(mmStr||'00').padStart(2,'0')}` + (ap ? ` ${ap}` : '');
            const parts: string[] = [];
            parts.push(`No reading exactly at ${hhmm} on ${dateText.replace(/[/.]/g,'-')} for device ${imeiMatch}.`);
            if (before) {
              parts.push(`Nearest BEFORE: ${before.latitude.toFixed(5)}, ${before.longitude.toFixed(5)} at ${new Date(before.created_at).toLocaleString()}`);
            }
            if (after) {
              parts.push(`Nearest AFTER: ${after.latitude.toFixed(5)}, ${after.longitude.toFixed(5)} at ${new Date(after.created_at).toLocaleString()}`);
            }
            return { response: parts.join(' '), data: [before, after].filter(Boolean) as any, intent: { type: 'point_at_time_on_date', device_id: imeiMatch, ymd, exact: false } };
          }
        }
      } catch {}
    }

    // 0.1) Relative time window: "between HH[:MM] and HH[:MM] yesterday"
    const betweenYesterday = query.match(/between\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+and\s+(\d{1,2})(?::(\d{2}))?\s*(am|pm)?\s+yesterday/i);
    if (betweenYesterday) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        if (imeiMatch) {
          // Ownership check
          const allowed = await this.getMyDeviceIdSet();
          const own = getDeviceIdVariations(imeiMatch).some(v => allowed.has(v));
          if (!own) {
            return { response: `The device ${imeiMatch} is not part of your organization.`, data: [], intent: { type: 'not_owned', device_id: imeiMatch } };
          }
          const timeZone = 'Asia/Jerusalem';
          const [ , sh, sm, sampm, eh, em, eampm ] = betweenYesterday;
          const to24 = (hStr?: string, mStr?: string, ap?: string) => {
            let h = Math.max(0, Math.min(23, parseInt(hStr || '0')));
            let m = Math.max(0, Math.min(59, parseInt(mStr || '0')));
            if (ap) {
              const apLower = ap.toLowerCase();
              if (apLower === 'pm' && h < 12) h += 12;
              if (apLower === 'am' && h === 12) h = 0;
            }
            return { h: String(h).padStart(2, '0'), m: String(m).padStart(2, '0') };
          };
          const s = to24(sh, sm, sampm);
          const e = to24(eh, em, eampm);
          const ymd = getYesterdayYmd(timeZone);
          const startLocal = `${ymd}T${s.h}:${s.m}:00`;
          const endLocal = `${ymd}T${e.h}:${e.m}:59`;
          const startUtc = localTimeInZoneToUtcIso(startLocal, timeZone);
          const endUtc = localTimeInZoneToUtcIso(endLocal, timeZone);

          const variations = getDeviceIdVariations(imeiMatch);
          let q = supabase
            .from('gps_data')
            .select('*')
            .gte('created_at', startUtc)
            .lte('created_at', endUtc)
            .order('created_at', { ascending: true })
            .limit(1000);
          if (variations.length > 1) q = q.in('device_id', variations); else q = q.eq('device_id', imeiMatch);
          const { data, error } = await q;
          if (error) throw error;
          const points = (data || []) as unknown as GpsData[];
          if (points.length > 0) {
            const first = points[0];
            const last = points[points.length - 1];
            const resp = `Found ${points.length} locations for device ${imeiMatch} between ${sh}${sm?':'+sm:''}${sampm?(' '+sampm):''} and ${eh}${em?':'+em:''}${eampm?(' '+eampm):''} yesterday. First at ${first.latitude.toFixed(5)}, ${first.longitude.toFixed(5)} at ${new Date(first.created_at).toLocaleString()} — last at ${last.latitude.toFixed(5)}, ${last.longitude.toFixed(5)} at ${new Date(last.created_at).toLocaleString()}.`;
            return { response: resp, data: points, intent: { type: 'history_by_time_range', device_id: imeiMatch, ymd, start: startUtc, end: endUtc } };
          } else {
            return { response: `No locations found for device ${imeiMatch} in the requested time window yesterday.`, data: [], intent: { type: 'history_by_time_range', device_id: imeiMatch, ymd, start: startUtc, end: endUtc, found: false } };
          }
        }
      } catch {}
    }
    const deviceCountPatterns = [
      /how\s+many\s+devices/i,
      /devices?\s+do\s+i\s+have/i,
      /count\s+devices/i,
      /מספר\s+המכשירים/i,
      /כמה\s+מכשירים/i
    ];
    if (deviceCountPatterns.some((p) => p.test(query))) {
      try {
        // Prefer counting devices by customer_id to include all org devices
        let count = 0;
        try {
          const { data: auth } = await supabase.auth.getUser();
          let cid: string | null = null;
          if (auth?.user?.id) {
            const { data: prof } = await supabase
              .from('user_profiles')
              .select('customer_id')
              .eq('id', auth.user.id)
              .single();
            cid = prof?.customer_id || null;
          }
          if (cid) {
            const { count: c } = await supabase
              .from('device_settings')
              .select('device_id', { count: 'exact', head: true })
              .in('user_id', (
                await supabase.from('user_profiles').select('id').eq('customer_id', cid)
              ).data?.map((u: any) => u.id) || []);
            count = c || 0;
          }
        } catch {}
        if (count === 0) {
          const { data: devices, error } = await supabase.rpc('get_user_devices');
          if (error) throw error;
          count = (devices || []).length;
        }
        return {
          response: `You have ${count} device${count === 1 ? '' : 's'} in your account.`,
          data: [],
          intent: { type: 'device_count', value: count }
        };
      } catch (e) {
        // If RPC fails, continue to Edge Function fallback
      }
    }

    // Intent: last known location (general or for a specific device)
    const lastLocationPatterns = [
      /last\s+(known\s+)?location/i,
      /where\s+is\s+(my\s+)?device/i,
      /איפה\s+המכשיר/i,
      /מיקום\s+אחרון/i
    ];
    if (lastLocationPatterns.some((p) => p.test(query))) {
      try {
        const imeiMatch = (query.match(/\d{10,15}/g) || [])[0];
        let deviceIds: string[] = [];
        if (imeiMatch) {
          deviceIds = [imeiMatch];
        } else {
          const { data: myDevices } = await supabase.rpc('get_user_devices');
          deviceIds = (myDevices || []).map((d: any) => d.device_id);
        }
        if (deviceIds.length === 0) {
          return {
            response: 'No devices found in your account.',
            data: [],
            intent: { type: 'last_location', found: false }
          };
        }
        const { data: gps } = await supabase
          .from('gps_data')
          .select('*')
          .in('device_id', deviceIds)
          .order('created_at', { ascending: false })
          .limit(1);
        if (gps && gps.length > 0) {
          const g: any = gps[0];
          const ts = new Date(g.created_at).toLocaleString();
          const address = (g as any).address || '';
          const msg = `Last known location for device ${g.device_id}: ${g.latitude}, ${g.longitude}${address ? ' (' + address + ')' : ''} at ${ts}.`;
          return { response: msg, data: gps as any, intent: { type: 'last_location', device_id: g.device_id } };
        }
      } catch (e) {
        // ignore and let Edge handle
      }
    }

    // Always use the Edge Function (it's deployed and working)
    try {
      const { data: auth } = await supabase.auth.getUser();
      let customerId: string | null = null;
      if (auth?.user?.id) {
        const { data: prof } = await supabase
          .from('user_profiles')
          .select('customer_id')
          .eq('id', auth.user.id)
          .single();
        customerId = prof?.customer_id || null;
      }
      const { data, error } = await supabase.functions.invoke('ai-query', {
        body: { 
          query, 
          userId: auth?.user?.id || null,
          customerId,
          // Only send conversation history if it's a follow-up question
          ...(isFollowUp && conversationHistory ? { conversationHistory, isFollowUp } : {})
        }
      });
      
      if (error) {
        console.error('AI query error:', error);
        throw error;
      }
      // If Edge function returned generic "no recent location data" message for a count question, override with deterministic count
      if (deviceCountPatterns.some((p) => p.test(query))) {
        try {
          const { data: devices } = await supabase.rpc('get_user_devices');
          const count = (devices || []).length;
          return {
            response: `You have ${count} device${count === 1 ? '' : 's'} in your account.`,
            data: [],
            intent: { type: 'device_count', value: count }
          };
        } catch {}
      }

      return data;
    } catch (error) {
      console.error('Failed to query AI via supabase.functions.invoke:', error);
      // Secondary fallback: call the Edge Function directly (no-verify-jwt)
      try {
        // Use env if available; otherwise fall back to the known production ref
        const projectUrl = process.env.REACT_APP_SUPABASE_URL || 'https://xlsxtvipjkctvwkzahmc.supabase.co';
        // Expect: https://<ref>.supabase.co → extract <ref>
        const refMatch = projectUrl.match(/^https?:\/\/([^.]+)\.supabase\.co/i);
        const projectRef = refMatch ? refMatch[1] : 'xlsxtvipjkctvwkzahmc';
        const directUrl = `https://${projectRef}.functions.supabase.co/ai-query`;
        const res = await fetch(directUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query, userId: null })
        });
        if (res.ok) {
          const data = await res.json();
          return data;
        }
      } catch (e) {
        console.error('Direct Edge call fallback failed:', e);
      }
      // Final fallback to mock so UI still responds
      const { mockAIQuery } = await import('./mock-ai');
      return mockAIQuery(query);
    }
  }

  async getGeofences(): Promise<Geofence[]> {
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    // Fetch only user's own geofences (RLS will enforce this too)
    const { data, error } = await supabase
      .from('geofences')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching geofences:', error);
      return [];
    }

    return data || [];
  }

  async createGeofence(geofence: Partial<Geofence>): Promise<Geofence | null> {
    console.log('Creating geofence with data:', geofence);
    
    // Get current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      console.error('No authenticated user found');
      return null;
    }
    
    const { data, error } = await supabase
      .from('geofences')
      .insert({
        name: geofence.name,
        latitude: geofence.latitude,
        longitude: geofence.longitude,
        radius: geofence.radius,
        active: geofence.active ?? true,
        devices: geofence.devices,
        user_id: user.id  // Always set user_id
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating geofence:', error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return null;
    }

    console.log('Geofence created successfully:', data);
    return data;
  }

  async updateGeofence(id: string, updates: Partial<Geofence>): Promise<Geofence | null> {
    const { data, error } = await supabase
      .from('geofences')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating geofence:', error);
      return null;
    }

    return data;
  }

  async deleteGeofence(id: string): Promise<boolean> {
    const { error } = await supabase
      .from('geofences')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting geofence:', error);
      return false;
    }

    return true;
  }

  async checkGeofenceViolations(deviceId: string, lat: number, lng: number): Promise<Alert[]> {
    const geofences = await this.getGeofences();
    const alerts: Alert[] = [];

    for (const geofence of geofences) {
      if (!geofence.active) continue;
      if (geofence.devices && !geofence.devices.includes(deviceId)) continue;

      const distance = this.calculateDistance(lat, lng, geofence.latitude, geofence.longitude);
      const isInside = distance <= geofence.radius;

      // This is simplified - in production, you'd track previous states
      // to determine entry/exit events
      if (!isInside) {
        alerts.push({
          id: `geofence-${geofence.id}-${deviceId}-${Date.now()}`,
          device_id: deviceId,
          type: 'geofence',
          message: `Device ${deviceId} is outside geofence "${geofence.name}"`,
          severity: 'medium',
          timestamp: new Date().toISOString(),
          acknowledged: false,
        });
      }
    }

    return alerts;
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c;
  }
}

// Helper: parse explicit date to UTC start/end with timezone awareness
function parseExplicitDateRange(dateText: string, timeZone: string) {
  const ymd = dateText.match(/^(20\d{2})[-/.](0[1-9]|1[0-2])[-/.](0[1-9]|[12]\d|3[01])$/);
  const dmy = dateText.match(/^(0[1-9]|[12]\d|3[01])[-/.](0[1-9]|1[0-2])[-/.](20\d{2})$/);
  let y: number | null = null, m: number | null = null, d: number | null = null;
  if (ymd) { y = parseInt(ymd[1]); m = parseInt(ymd[2]); d = parseInt(ymd[3]); }
  if (dmy) { d = parseInt(dmy[1]); m = parseInt(dmy[2]); y = parseInt(dmy[3]); }
  if (!y || !m || !d) return null;
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  const startLocal = `${y}-${mm}-${dd}T00:00:00`;
  const endLocal = `${y}-${mm}-${dd}T23:59:59`;
  return {
    start: localTimeInZoneToUtcIso(startLocal, timeZone),
    end: localTimeInZoneToUtcIso(endLocal, timeZone)
  };
}

function localTimeInZoneToUtcIso(localIsoNoTz: string, timeZone: string): string {
  const [datePart, timePart] = localIsoNoTz.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm, ss] = timePart.split(':').map(Number);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
  });
  const guessUtc = Date.UTC(y, (m - 1), d, hh, mm, ss);
  const parts = fmt.formatToParts(new Date(guessUtc));
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value || '0');
  const tzYear = get('year');
  const tzMonth = get('month');
  const tzDay = get('day');
  const tzHour = get('hour');
  const tzMinute = get('minute');
  const tzSecond = get('second');
  const desiredLocalMs = Date.UTC(y, (m - 1), d, hh, mm, ss);
  const currentLocalMs = Date.UTC(tzYear, (tzMonth - 1), tzDay, tzHour, tzMinute, tzSecond);
  const delta = desiredLocalMs - currentLocalMs;
  const correctedUtcMs = guessUtc + delta;
  return new Date(correctedUtcMs).toISOString();
}

function getYesterdayYmd(timeZone: string): string {
  const now = new Date();
  const yesterdayMs = now.getTime() - 24 * 60 * 60 * 1000;
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(new Date(yesterdayMs)); // returns YYYY-MM-DD in en-CA
}

function getTodayYmd(timeZone: string): string {
  const now = new Date();
  const fmt = new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' });
  return fmt.format(now);
}

// Haversine distance in kilometers
function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function parseDateTextToYmd(dateText: string): string | null {
  const cleaned = dateText.trim();
  const ymd = cleaned.match(/^(20\d{2})[-/.](0?[1-9]|1[0-2])[-/.](0?[1-9]|[12]\d|3[01])$/);
  const dmy = cleaned.match(/^(0?[1-9]|[12]\d|3[01])[-/.](0?[1-9]|1[0-2])[-/.](20\d{2})$/);
  let y: number | null = null, m: number | null = null, d: number | null = null;
  if (ymd) { y = parseInt(ymd[1]); m = parseInt(ymd[2]); d = parseInt(ymd[3]); }
  if (dmy) { d = parseInt(dmy[1]); m = parseInt(dmy[2]); y = parseInt(dmy[3]); }
  if (!y || !m || !d) return null;
  const mm = String(m).padStart(2, '0');
  const dd = String(d).padStart(2, '0');
  return `${y}-${mm}-${dd}`;
}

export const traqPackAPI = new TraqPackAPI();

// Simple city bounds and matchers for Tel Aviv checks
const TEL_AVIV_BOUNDS = {
  minLat: 31.98,
  maxLat: 32.16,
  minLng: 34.70,
  maxLng: 34.90
};
const TEL_AVIV_SYNONYMS = [
  'tel aviv', 'tel-aviv', 'tel aviv-yafo', 'tel-aviv-yafo', 'tel aviv jaffa', 'tel-aviv-jaffa',
  'tel aviv-yafo', 'תל אביב', 'תל-אביב', 'ת\u200eל אביב'
];
function isInTelAviv(lat?: number, lng?: number, address?: string): boolean {
  const addr = (address || '').toLowerCase();
  if (addr && TEL_AVIV_SYNONYMS.some(s => addr.includes(s))) return true;
  if (typeof lat === 'number' && typeof lng === 'number') {
    return (
      lat >= TEL_AVIV_BOUNDS.minLat && lat <= TEL_AVIV_BOUNDS.maxLat &&
      lng >= TEL_AVIV_BOUNDS.minLng && lng <= TEL_AVIV_BOUNDS.maxLng
    );
  }
  return false;
}